<template>
  <nav ref="navEl" :class="['rct-nav', { 'rct-nav--scrolled': scrolled || menuOpen }]">
    <a :href="logoHref" class="rct-nav__logo">
      <img :src="logoLight" alt="RidgeCodeTech" class="rct-nav__logo-img">
      <span class="rct-nav__brand">RidgeCodeTech</span>
    </a>
    <ul :class="['rct-nav__links', { 'rct-nav__links--open': menuOpen }]">
      <li v-for="link in links" :key="link.href"><a :href="link.href" @click="menuOpen = false">{{ link.label }}</a></li>
    </ul>
    <button class="rct-nav__toggle" :aria-expanded="menuOpen" aria-label="Toggle navigation" @click="menuOpen = !menuOpen">
      <span></span><span></span><span></span>
    </button>
    <slot name="actions" />
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import logoLight from '../assets/logo-light.png';
withDefaults(defineProps<{ links: { href: string; label: string }[]; logoHref?: string }>(), { logoHref: '/' });
const navEl    = ref<HTMLElement | null>(null);
const scrolled = ref(false);
const menuOpen = ref(false);
const onScroll  = () => { scrolled.value = window.scrollY > 60; };
const onOutside = (e: MouseEvent) => {
  if (navEl.value && !navEl.value.contains(e.target as Node)) menuOpen.value = false;
};
const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') menuOpen.value = false; };
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('click', onOutside);
  document.addEventListener('keydown', onKey);
});
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  document.removeEventListener('click', onOutside);
  document.removeEventListener('keydown', onKey);
});
</script>

<style>
.rct-nav__signin {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e1ecff;
  border: 1px solid rgba(190,205,234,0.3);
  border-radius: 3px;
  padding: 0.4rem 0.9rem;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}
.rct-nav__signin:hover {
  color: #fff;
  border-color: rgba(190,205,234,0.6);
  background: rgba(190,205,234,0.08);
}
</style>

<style scoped>
.rct-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.25rem 4rem;
  transition: background 0.3s;
}
.rct-nav--scrolled { background: rgba(9,26,56,0.92); backdrop-filter: blur(12px); }
.rct-nav__logo { display: flex; align-items: center; gap: 0.75rem; text-decoration: none; }
.rct-nav__logo-img { height: 22px; width: auto; display: block; }
.rct-nav__brand { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 1rem; color: #e1ecff; }
.rct-nav__links {
  position: absolute; left: 50%; transform: translateX(-50%);
  display: flex; gap: 2rem; list-style: none;
}
.rct-nav__links a { font-size: 0.875rem; color: #becdea; text-decoration: none; transition: color 0.2s; }
.rct-nav__links a:hover { color: #fff; }

.rct-nav__toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  padding: 6px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1;
}
.rct-nav__toggle span {
  display: block;
  width: 22px;
  height: 2px;
  background: #e1ecff;
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.rct-nav__toggle[aria-expanded="true"] span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.rct-nav__toggle[aria-expanded="true"] span:nth-child(2) { opacity: 0; }
.rct-nav__toggle[aria-expanded="true"] span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

@media (max-width: 768px) {
  .rct-nav { padding: 1rem 1.25rem; }
  .rct-nav__toggle { display: flex; }
  .rct-nav__links {
    position: absolute;
    top: 100%;
    left: 0; right: 0;
    transform: none;
    flex-direction: column;
    gap: 0;
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.35s ease;
    background: rgba(9,26,56,0.97);
    backdrop-filter: blur(12px);
    border-top: 1px solid #1a3a6e;
  }
  .rct-nav__links--open { max-height: 400px; }
  .rct-nav__links li { width: 100%; }
  .rct-nav__links a {
    display: block;
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
    border-bottom: 1px solid rgba(26,58,110,0.6);
  }
  .rct-nav__links li:last-child a { border-bottom: none; }
}
</style>
